/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps, withRouter } from 'react-router-dom';

import { Query } from '@apollo/client/react/components';

import FullPageHomeLoader from '../components/FullPageHomeLoader';
import Layout, { hasAnyUnauthenticatedError } from '../components/Layout';
import { PartnerContext } from '../config/PartnerContext';
import { GET_ORGANIZATION, IGetOrganization } from '../graphql/queries/GetOrganization';
import {
  GET_SESSION_DATA,
  IGetSessionData,
  ISessionHuman,
} from '../graphql/queries/GetSessionData';
import { WEB_API_URL } from '../helpers/globals';
import { ERROR_PAGE_ROUTE, PARTNER_AGENT_REFERRAL_NETWORK_DASHBOARD_ROUTE } from './routes';
import UnauthorizedAccessErrorPage from './UnauthorizedAccessErrorPage';

export type PrivateRouteProps = Pick<RouteProps, Exclude<keyof RouteProps, 'component'>> & {
  isAuthorized?: (sessionHuman: ISessionHuman) => boolean;
  component?: RouteProps['component'] & PrivateRouteComponent;
  components?: Array<{ component: RouteProps['component'] & PrivateRouteComponent; path: string }>;
  renderLogo?: () => JSX.Element | null;
} & RouteComponentProps;

export type PrivateRouteComponent = {
  isAuthorized?: PrivateRouteProps['isAuthorized'];
  dashboardName?: string;
};

class PrivateRoute extends React.Component<PrivateRouteProps> {
  componentDidMount() {
    this.updateBodyBackground();
  }

  componentDidUpdate(prevProps: PrivateRouteProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.updateBodyBackground();
    }
  }

  componentWillUnmount() {
    document.body.style.removeProperty('background-color');
  }

  updateBodyBackground() {
    const path = this.props.location.pathname;
    if (path === PARTNER_AGENT_REFERRAL_NETWORK_DASHBOARD_ROUTE) {
      document.body.style.backgroundColor = 'white';
    } else {
      document.body.style.backgroundColor = '#f4f8fb';
    }
  }

  public static contextType: typeof PartnerContext = PartnerContext;

  public renderLoader = () => <FullPageHomeLoader />;
  public renderNav = () => <div />;
  public renderSide = () => <div />;

  public render() {
    const loadingScreen = (
      <Layout
        renderLogo={this.props.renderLogo}
        renderContent={this.renderLoader}
        renderNav={this.renderNav}
        renderSide={this.renderSide}
      />
    );
    // @ts-expect-error - @partnerships please update the context type (error: Property setPartnerIdentifier does not exist on type 'unknown')
    const { setPartnerIdentifier } = this.context;

    return (
      <Query<IGetSessionData> query={GET_SESSION_DATA}>
        {({ data, error, loading }) => {
          if (loading) {
            return loadingScreen;
          }
          if (error) {
            if (hasAnyUnauthenticatedError(error.graphQLErrors)) {
              return <RedirectToLogin />;
            }
            return <Redirect to={ERROR_PAGE_ROUTE} />;
          }
          if (!data || !data.session || !data.session.logged_in || !data.session.human) {
            return <RedirectToLogin />;
          }
          let unauthorized = true;
          let dashboardName: string | undefined;
          let routeOrRedirect: JSX.Element;

          if (this.props.component) {
            unauthorized = this.props.component.isAuthorized
              ? !this.props.component.isAuthorized(data!.session!.human!)
              : false;
            dashboardName = this.props.component.dashboardName;
            routeOrRedirect = <Route {...this.props} />;
          } else if (this.props.components) {
            const componentToRender = this.props.components.find(
              (c) =>
                !!(c.component.isAuthorized && c.component.isAuthorized(data!.session!.human!)),
            );
            unauthorized = !componentToRender;
            if (componentToRender) {
              routeOrRedirect = <Redirect to={componentToRender!.path} />;
            }
          }
          if (unauthorized) {
            return (
              <UnauthorizedAccessErrorPage
                dashboardName={dashboardName}
                userEmail={data.session.human.email}
                loadingScreen={loadingScreen}
              />
            );
          }
          return (
            <Query<IGetOrganization> query={GET_ORGANIZATION}>
              {({ data, error }) => {
                if (error) {
                  return <Redirect to={ERROR_PAGE_ROUTE} />;
                }
                if (data && data.organization?.identifier) {
                  setPartnerIdentifier(data.organization?.identifier);
                  return routeOrRedirect;
                }
                return null;
              }}
            </Query>
          );
        }}
      </Query>
    );
  }
}

export const RedirectToLogin = () => {
  React.useEffect(() => {
    const loginAndRedirectURL = `${WEB_API_URL}/login?redirectTo=${window.location.href}`;
    window.location.assign(loginAndRedirectURL);
  });
  return null;
};

export default withRouter(PrivateRoute);
